import type { ReactElement } from "react";
import type { SPACING } from "odyssey-shared";
import { Button, logical } from "../ui/kit/index.ts";
import { say } from "../words.ts";
import { EntryScreen } from "./entry-screen.tsx";
import { LOGIN_METRICS } from "./login-screen.tsx";

/**
 * The returning player's prompt (P1.4-T7, ADR-075 decision 9; the design's `ReturningPrompt` in
 * `ui_kits/game_client/Entry.jsx`, a visual reference): a tab that holds no grant in a browser kept logged in
 * resumes the kept session and shows, in the entry screens' layout and the log in form's window width, "Logged
 * in as" and the account's email, with Log out and Continue under it at the right, so a returning player
 * neither types a password nor joins without choosing to. Continue goes on with the resumed grant as a login
 * would; Log out ends this device's keep and shows the log in form. A long email wraps anywhere inside the
 * window rather than widening it.
 */

/**
 * A spacing step of the token table, checked at compile time.
 *
 * @param step One of `SPACING`.
 * @returns The step in logical pixels.
 */
function space(step: (typeof SPACING)[number]): number {
  return step;
}

/**
 * The prompt's measurements in logical pixels: the log in form's window, the gap between the line and the
 * button row, and between the buttons, as the design's footer spaces them.
 */
export const RETURNING_METRICS = {
  window: LOGIN_METRICS.window,
  gap: space(6),
  actionGap: space(4),
} as const;

/**
 * What the prompt shows and whom it tells: the account's email, whether a logout is out, and the player's
 * two actions.
 */
export interface ReturningScreenProps {
  readonly email: string;
  readonly busy: boolean;
  readonly onContinue: () => void;
  readonly onLogOut: () => void;
}

/**
 * The returning player's prompt.
 *
 * @param props The email, whether a logout is out, and Continue's and Log out's actions.
 * @returns The screen.
 */
export function ReturningScreen({ email, busy, onContinue, onLogOut }: ReturningScreenProps): ReactElement {
  return (
    <EntryScreen mode="returning" layout={`returning:${email}`} title={say("account.returning.title")} width={RETURNING_METRICS.window}>
      <div className="ot-login__form" data-login="returning" style={{ gap: logical(RETURNING_METRICS.gap) }}>
        <p className="ot-returning__line">
          {say("account.returning.loggedInAs")}{" "}
          <b className="ot-returning__email" data-login="email">
            {email}
          </b>
        </p>
        <div className="ot-returning__actions" data-login="actions" style={{ gap: logical(RETURNING_METRICS.actionGap) }}>
          <Button busy={busy} busyLabel={say("account.returning.busy")} onClick={onLogOut}>
            {say("account.naming.switch")}
          </Button>
          <Button variant="primary" disabled={busy} onClick={onContinue}>
            {say("account.returning.continue")}
          </Button>
        </div>
      </div>
    </EntryScreen>
  );
}
